import {ChangeDetectorRef, Component, OnDestroy, OnInit} from "@angular/core";
import {AuthApiService} from "../../auth/auth-api.service";
import {ActivatedRoute, Router} from "@angular/router";
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {
    FUIAlertAction,
    FUIAlertActions,
    FUIAlertService, FUIBreakpoints,
    FUIModalsService,
    FUIResponsiveService,
    FUISpinnersService,
    FUIStackedPageService
} from "@fedelta-pos/ui";
import {ErrorHandlerService} from "../../../error-handler.service";
import {CustomerInstanceService} from "../../customer-instance/customer-instance.service";
import {CustomerInstanceVenueData} from "../customer-instance-venue.data";
import {StatusPanelService} from "../../content-layout/status-panel/status-panel.service";
import {SignupService} from "../../signup/signup.service";
import {CustomerInstanceVenueService} from "../customer-instance-venue.service";
import {CustomerInstanceVenueModel} from "../customer-instance-venue.model";
import {Status} from "../../content-layout/status-panel/status-panel.component";
import {UserService} from "../../auth/user.service";
import {UserData} from "../../auth/user.data";
import {
    CustomerInstanceSearchPageComponent
} from "../../customer-instance/customer-instance-search/customer-instance-search-page.component";
import {SignUpAction, SignupModalComponent} from "../signup/signup-modal.component";
import {SignupPageModalComponent} from "../../signup/signup-page-modal/signup-page-modal.component";
import {CustomerPortalService} from "../../customer-portal/customer-portal.service";
import {SignupUrlComponent} from "../signup/signup-url.component";
import {AppPaymentAccountService} from "../../app-payment-account/app-payment-account.service";
import {
    AppPaymentAccountPageModalComponent
} from "../../app-payment-account/app-payment-account-page-modal/app-payment-account-page-modal.component";
import {VenueContactData} from "../venue-contact/venue-contact.data";
import {ClickUpService} from "../../clickup/clickup.service";
import {ConfigService} from "../../config/config.service";
import {BehaviorSubject, first, Subscription, timer} from "rxjs";

export function initialStartTermDateValidator(limitDaysAhead: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (control.value) {
            const date: Date = control.value;
            date.setHours(0, 0, 0, 0);
            const today: Date = new Date();
            today.setHours(0, 0, 0, 0);
            if (date.getTime() < today.getTime()) {
                return {validationMessages: [{messageKey: "Date cannot be in the past"}]};
            } else {
                const limitDate: Date = new Date();
                limitDate.setHours(0, 0, 0, 0);
                limitDate.setDate(limitDate.getDate() + limitDaysAhead);
                if (date.getTime() > limitDate.getTime()) {
                    return {validationMessages: [{messageKey: "Date cannot exceed " + limitDaysAhead + " days from today"}]};
                }
                return null;
            }
        } else {
            return null;
        }
    };
}

@Component({
    selector: "fhm-customer-instance-venue-edit-page",
    styles: [":host {display: flex; width: 100%;}"],
    templateUrl: "./customer-instance-venue-edit-page.component.html",
    providers: [CustomerInstanceService,
        CustomerInstanceVenueService,
        CustomerInstanceVenueModel,
        StatusPanelService,
        SignupService,
        CustomerPortalService,
        UserService,
        AppPaymentAccountService,
        ClickUpService,
        ConfigService
    ],
    standalone: false
})
export class CustomerInstanceVenueEditPageComponent implements OnInit, OnDestroy {

    Status = Status;
    UserData = UserData;
    VenueContactData = VenueContactData;

    CustomerInstanceVenueData = CustomerInstanceVenueData;

    customerClientId: string;
    customerInstanceId: string;
    venueId: string;

    oldVenue: CustomerInstanceVenueData;

    editingVenue: boolean;

    formGroupVenue: FormGroup;
    formGroupCompany: FormGroup;
    formGroupAddress: FormGroup;
    formGroupPrimaryContact: FormGroup;
    formGroupSiteContact: FormGroup;
    formGroupServices: FormGroup;
    formGroupPlan: FormGroup;

    loggedUser: UserData;

    smallScreen: boolean = true;

    private subscriptions: Subscription = new Subscription();
    private refreshSubscription: Subscription;
    private destroyed: boolean;

    sourceSalesSignUpSearch: boolean;

    serverConfig: any;
    daysLimitInitialStartTerm: number;


    constructor(
        private auth: AuthApiService,
        private router: Router,
        private route: ActivatedRoute,
        private errorHandler: ErrorHandlerService,
        private spinnersService: FUISpinnersService,
        private formBuilder: FormBuilder,
        private customerInstanceVenueService: CustomerInstanceVenueService,
        public model: CustomerInstanceVenueModel,
        private modalsService: FUIModalsService,
        private signupService: SignupService,
        private customerPortalService: CustomerPortalService,
        private alertService: FUIAlertService,
        private responsiveService: FUIResponsiveService,
        private changeDetectorRef: ChangeDetectorRef,
        private userService: UserService,
        private stackedPageService: FUIStackedPageService,
        private appPaymentAccountService: AppPaymentAccountService,
        private configService: ConfigService) {
    }

    ngOnInit(): void {
        this.route.paramMap.subscribe((params) => {
            this.route.queryParams.subscribe(async (queryParams) => {
                this.loggedUser = await this.userService.getLoggedUser();
                this.model.salesRep = UserData.isSalesRep(this.loggedUser);
                this.model.loggedUser = this.loggedUser;

                this.serverConfig = await this.configService.getServerConfig();
                this.daysLimitInitialStartTerm = this.serverConfig[ConfigService.DAYS_LIMIT_INITIAL_START_TERM];

                this.customerInstanceId = queryParams["instanceId"];
                this.customerClientId = queryParams["clientId"];

                this.sourceSalesSignUpSearch = "true" === queryParams["signUp"];

                this.venueId = params.get("venueId");

                this.createFormGroup();

                if (this.venueId === "new") {
                    this.model.new();
                    this.model.data.venue.salesRep = this.auth.getEmail();
                    this.model.data.venue.instanceId = this.customerInstanceId;
                    this.editingVenue = true;
                } else {
                    this.load();
                }
            });
        });

        this.subscriptions.add(this.model.data$.subscribe(() => {
            this.changeDetectorRef.detectChanges();
            this.editingVenue = this.model.draft;
        }));

        this.smallScreen = this.responsiveService.breakpointApplies(FUIBreakpoints.smallDown);
        this.subscriptions.add(this.responsiveService.windowWidth$.subscribe(value => {
            this.smallScreen = this.responsiveService.breakpointApplies(FUIBreakpoints.smallDown);
            this.changeDetectorRef.markForCheck();
        }));

        this.initRefresh();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        if (this.refreshSubscription) {
            this.refreshSubscription.unsubscribe();
            this.destroyed = true;
        }
    }

    createFormGroup(): void {
        this.formGroupVenue = this.formBuilder.group({
            name: ["", [Validators.required, Validators.maxLength(35)]]
        });
        this.formGroupVenue.get("name")["required$"] = new BehaviorSubject(true);

        this.formGroupCompany = this.formBuilder.group({
            abn: ["", [Validators.required, Validators.minLength(11), Validators.maxLength(11)]],
            phone: ["", [Validators.required, Validators.maxLength(15)]],
            name: ["", [Validators.required, Validators.maxLength(60)]]
        });
        this.formGroupCompany.get("abn")["required$"] = new BehaviorSubject(true);
        this.formGroupCompany.get("phone")["required$"] = new BehaviorSubject(true);
        this.formGroupCompany.get("name")["required$"] = new BehaviorSubject(true);

        this.formGroupAddress = this.formBuilder.group({
            addressStreet: ["", [Validators.required, Validators.maxLength(60)]],
            addressCity: ["", [Validators.required, Validators.maxLength(30)]],
            addressPostcode: ["", [Validators.required, Validators.maxLength(10)]]
        });
        this.formGroupAddress.get("addressStreet")["required$"] = new BehaviorSubject(true);
        this.formGroupAddress.get("addressCity")["required$"] = new BehaviorSubject(true);
        this.formGroupAddress.get("addressPostcode")["required$"] = new BehaviorSubject(true);

        this.formGroupPrimaryContact = this.formBuilder.group({
            firstName: ["", [Validators.required, Validators.maxLength(30)]],
            lastName: ["", [Validators.required, Validators.maxLength(30)]],
            email: ["", [Validators.required, Validators.email, Validators.maxLength(60)]]
        });
        this.formGroupPrimaryContact.get("firstName")["required$"] = new BehaviorSubject(true);
        this.formGroupPrimaryContact.get("lastName")["required$"] = new BehaviorSubject(true);
        this.formGroupPrimaryContact.get("email")["required$"] = new BehaviorSubject(true);

        this.formGroupSiteContact = this.formBuilder.group({
            firstName: [""],
            lastName: [""],
            email: [""]
        });

        this.formGroupServices = this.formBuilder.group({
            tables: ["", [Validators.required]]
        });
        this.formGroupServices.get("tables")["required$"] = new BehaviorSubject(true);

        this.formGroupPlan = this.formBuilder.group({
            initialTermStartDate: ["", [initialStartTermDateValidator(this.daysLimitInitialStartTerm)]]
        });
    }

    async load(): Promise<void> {
        this.spinnersService.open();
        try {
            await this.model.load(this.venueId);
            this.editingVenue = this.model.draft;
        } catch (error) {
            this.errorHandler.handleErrorResponse(error);
        } finally {
            this.spinnersService.close();
            setTimeout(() => this.responsiveService.emitWindowWidthChange(), 100);
        }
    }

    get title(): string {
        if (!this.model.venueId) {
            return "New Venue";
        }

        if (!this.model.data.venue) {
            return "";
        }

        if (!this.model.data.venue.id) {
            return "New Venue";
        }

        return this.model.data.instance ? (this.model.data.instance.clientId + " / " + this.model.data.venue.name)
            : this.model.data.venue.name;
    }


    get signUpReady(): boolean {
        return !this.model.draft;
    }

    async refresh(): Promise<void> {
        this.spinnersService.open();
        try {
            await this.model.refresh();
        } catch (error) {
            this.errorHandler.handleErrorResponse(error);
        } finally {
            this.spinnersService.close();
        }
    }

    done(): void {
        if (this.customerInstanceId) {
            this.router.navigate(["/main/customer-instance-edit/" + this.customerInstanceId]);
        } else {
            if (this.sourceSalesSignUpSearch) {
                this.router.navigate(["/main/sales-signup-search"]);
            } else {
                this.router.navigate(["/main/customer-instance-venue-search"]);
            }
        }
    }

    goToInstance(): void {
        this.router.navigate(["/main/customer-instance-edit/" + this.model.data.instance.id]);
    }

    async saveVenue(): Promise<void> {
        this.touchFromGroup(this.formGroupVenue);
        if (this.formGroupVenue.valid) {
            this.spinnersService.open();
            try {
                await this.customerInstanceVenueService.updateCustomerInstanceVenue(this.model.data.venue);
                this.editingVenue = false;
            } catch (error) {
                this.errorHandler.handleErrorResponse(error);
            } finally {
                this.spinnersService.close();
            }
        }
    }

    async saveAsDraft(): Promise<void> {
        this.touchFromGroup(this.formGroupVenue);
        this.touchFromGroup(this.formGroupPlan);
        if (!this.formGroupVenue.valid) {
            return;
        }
        if (!this.formGroupPlan.valid) {
            return;
        }
        this.spinnersService.open();
        try {
            await this.saveAll();
        } catch (error) {
            this.errorHandler.handleErrorResponse(error);
        } finally {
            this.spinnersService.close();
        }
    }

    async saveAll(): Promise<void> {
        let customerInstanceVenue: CustomerInstanceVenueData;
        if (this.model.data.venue.id) {
            customerInstanceVenue = await this.customerInstanceVenueService.updateCustomerInstanceVenue(this.model.data.venue);
        } else {
            customerInstanceVenue = await this.customerInstanceVenueService.createCustomerInstanceVenue(this.model.data.venue);
        }
        await this.customerInstanceVenueService.saveVenueCompany(customerInstanceVenue.id, this.model.data.company);
        await this.customerInstanceVenueService.saveVenueAddress(customerInstanceVenue.id, this.model.data.address);
        await this.customerInstanceVenueService.saveVenueServices(customerInstanceVenue.id, this.model.data.services);
        await this.customerInstanceVenueService.saveVenueContact(customerInstanceVenue.id, this.model.data.primaryContact);
        await this.customerInstanceVenueService.saveVenueContact(customerInstanceVenue.id, this.model.data.siteContact);
        await this.customerInstanceVenueService.updatePlan(customerInstanceVenue.id, this.model.data.venuePlan);
        await this.model.load(customerInstanceVenue.id);
        this.venueId = customerInstanceVenue.id;
    }

    edit(): void {
        this.oldVenue = Object.assign({}, this.model.data.venue);
        this.editingVenue = true;
    }

    cancelEdit(): void {
        this.model.data.venue = this.oldVenue;
        this.editingVenue = false;
    }

    async signInPerson(): Promise<void> {
        this.spinnersService.open();
        let url: string;
        try {
            url = await this.signupService.generateSetupUrl(this.venueId);
        } catch (e) {
            this.errorHandler.handleErrorResponse(e);
        } finally {
            this.spinnersService.close();
        }

        this.modalsService.create(SignupPageModalComponent).subscribe((modal: SignupPageModalComponent) => {
            modal.init(url);
            modal.closed.subscribe(async () => {
                this.modalsService.destroy(modal);
                this.refresh();
            });
        });
    }

    async getSignUpUrl(): Promise<void> {
        this.spinnersService.open();
        let url: string;
        try {
            url = await this.signupService.generateSetupUrl(this.venueId);
        } catch (e) {
            this.errorHandler.handleErrorResponse(e);
        } finally {
            this.spinnersService.close();
        }
        this.modalsService.create(SignupUrlComponent).subscribe((modal) => {
            modal.url = url;
            modal.title = "Sign Up URL";
        });
    }

    async openCustomerPortal(): Promise<void> {
        this.spinnersService.open();
        let url: string;
        try {
            url = await this.customerPortalService.generateCustomerPortalUrl(this.venueId);
            window.open(url, "_blank");
        } catch (e) {
            this.errorHandler.handleErrorResponse(e);
        } finally {
            this.spinnersService.close();
        }
    }

    async sendSignUpUrl(): Promise<void> {
        const action: FUIAlertAction = await this.alertService.showConfirmationOkCancel("fhm.customer-instance-venue.messages.sendSignUpUrl.title",
            "fhm.customer-instance-venue.messages.sendSignUpUrl.message").toPromise();
        if (action === FUIAlertActions.OK_ACTION) {
            this.spinnersService.open();
            try {
                await this.signupService.sendSignUpUrl(this.venueId);
                await this.model.refresh();
            } catch (e) {
                this.errorHandler.handleErrorResponse(e);
            } finally {
                this.spinnersService.close();
            }
        }
    }

    async openClickUp(): Promise<void> {
        window.open("https://app.clickup.com/t/" + this.model.data.buildTask.clickUpId, "_blank");
    }

    assignInstance(): void {
        this.stackedPageService.open(CustomerInstanceSearchPageComponent, {searchMode: true}).toPromise().then(async (data) => {
            try {
                if (data) {
                    await this.customerInstanceVenueService.assignInstanceToCustomerInstanceVenue(this.venueId, data);
                }
                await this.refresh();
            } catch (e) {
                this.errorHandler.handleErrorResponse(e);
            }
        });
    }

    touchFromGroup(formGroup: FormGroup): void {
        Object.keys((formGroup.controls)).forEach((key) => {
            formGroup.controls[key].markAsTouched();
            formGroup.controls[key].updateValueAndValidity();
        });
    }

    async complete(): Promise<void> {
        this.touchFromGroup(this.formGroupVenue);
        this.touchFromGroup(this.formGroupCompany);
        this.touchFromGroup(this.formGroupServices);
        this.touchFromGroup(this.formGroupAddress);
        this.touchFromGroup(this.formGroupPrimaryContact);
        this.touchFromGroup(this.formGroupPlan);
        if (this.formGroupVenue.valid && this.formGroupCompany.valid && this.formGroupServices.valid && this.formGroupAddress.valid
            && this.formGroupPrimaryContact.valid && this.formGroupPlan.valid) {

            this.modalsService.create(SignupModalComponent).subscribe((modal) => {
                modal.onConfirm.subscribe(async (result) => {
                    this.modalsService.destroy(modal);

                    if (result === SignUpAction.CANCEL) {
                        return;
                    }
                    this.spinnersService.open();
                    try {
                        this.model.data.venue.draft = false;
                        await this.saveAll();
                    } catch (error) {
                        this.errorHandler.handleErrorResponse(error);
                    } finally {
                        this.spinnersService.close();
                    }

                    this.editingVenue = false;

                    this.initRefresh();
                    if (result === SignUpAction.SEND_SIGN_UP) {
                        await this.signupService.sendSignUpUrl(this.venueId);
                        await this.refresh();
                    } else if (result === SignUpAction.SIGN_UP_PERSON) {
                        await this.signInPerson();
                    } // else sign up later
                });
            });
        } else {
            this.alertService.showError("fhm.common.prompt.requiredFields.heading",
                "fhm.common.prompt.requiredFields.message").subscribe(() => {
            });
        }
    }

    private initRefresh(): void {
        this.refreshSubscription = timer(5000).pipe(first()).subscribe(() => {
            if (this && !this.destroyed && !this.model.canEdit() && this.model.data.venue.deploymentStatus !== CustomerInstanceVenueData.DEPLOYMENT_STATUS_CHURNED && !this.model.draft) {
                this.model.refresh().then(() => this.initRefresh()).catch(this.initRefresh);
            }
        });
    }

    async openAppPaymentAccount(): Promise<void> {
        this.spinnersService.open();
        let url: string;
        try {
            url = await this.appPaymentAccountService.generateAppPaymentAccountUrl(this.venueId);
        } catch (e) {
            this.errorHandler.handleErrorResponse(e);
        } finally {
            this.spinnersService.close();
        }
        this.modalsService.create(AppPaymentAccountPageModalComponent).subscribe((modal) => {
            modal.init(url);
            modal.closed.subscribe(async () => {
                this.modalsService.destroy(modal);
                this.refresh();
            });
        });
    }

    async getSetupAppPaymentAccountUrl(): Promise<void> {
        this.spinnersService.open();
        let url: string;
        try {
            url = await this.appPaymentAccountService.generateAppPaymentAccountUrl(this.venueId);
        } catch (e) {
            this.errorHandler.handleErrorResponse(e);
        } finally {
            this.spinnersService.close();
        }
        this.modalsService.create(SignupUrlComponent).subscribe((modal) => {
            modal.url = url;
            modal.title = "Setup App Payment Account URL";
        });
    }

    async discontinueVenue(): Promise<void> {
        const action: FUIAlertAction = await this.alertService.showWarningOkCancel("fhm.customer-instance-venue.alert.discontinue.title",
            "fhm.customer-instance-venue.alert.discontinue.message").toPromise();
        if (action === FUIAlertActions.OK_ACTION) {
            this.spinnersService.open();
            try {
                await this.customerInstanceVenueService.discontinueVenue(this.model.venueId);
                await this.model.refresh();
                this.spinnersService.close();
            } catch (e) {
                this.spinnersService.close();
                this.errorHandler.handleErrorResponse(e);
            }
            await this.refresh();
        }
    }

    async recontinueVenue(): Promise<void> {
        const action: FUIAlertAction = await this.alertService.showWarningOkCancel("fhm.customer-instance-venue.alert.recontinue.title",
            "fhm.customer-instance-venue.alert.recontinue.message").toPromise();
        if (action === FUIAlertActions.OK_ACTION) {
            this.spinnersService.open();
            try {
                await this.customerInstanceVenueService.recontinueVenue(this.model.venueId);
                await this.model.refresh();
                this.spinnersService.close();
            } catch (e) {
                this.spinnersService.close();
                this.errorHandler.handleErrorResponse(e);
            }
            await this.refresh();
        }
    }
}
